<template>
  <v-card>
    <v-toolbar :dark="$vuetify.theme.dark" color="primary">
      <v-toolbar-title>
        {{ $t('Выбор победителя для торговой процедуры № {0}', [ lot.lotNumber ]) }}
      </v-toolbar-title>
    </v-toolbar>
    <general-error v-if="!!generalError" />
    <v-alert
        class="text-center"
        type="success"
        :value="success">
      {{ $t('Изменения сохранены') }}
    </v-alert>
    <v-card-text>
      <trade-result-divisible-volume
          v-if="isDivisibleVolume"
          :orders="orders"
          :is-fix-price="isFixPremium"
          :readonly="!isLotOwner(lot) && !isAdmin"
          :loading="isLoading" />
      <trade-result-full-volume
          v-else
          :orders="orders"
          :is-fix-price="isFixPremium"
          readonly
          :loading="isLoading" />
      <v-row>
        <v-col class="text-left" cols="12" md="6">
          <ValidationObserver ref="tradeResultType">
            <validatable-enum-combobox
                v-model="result.tradeResultType"
                v-test="{ combobox: 'tradeResultType' }"
                rules="required"
                :values-enum="tradeResultTypesEnum"
                :label="$t('Итог торгов')"
                :readonly="!canEdit" />
          </ValidationObserver>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions v-if="canEditResults">
      <v-btn
          id="toLotBtn"
          v-test="{ btn: 'toLot' }"
          class="ma-2"
          color="primary"
          @click.stop="goToLot">
        {{ $t('К торговой процедуре') }}
      </v-btn>
      <winner-selection-full-volume-dialog
          v-if="hasOrder && !isDivisibleVolume"
          :result="result"
          :has-result="hasResult"
          :is-fix-premium="isFixPremium"
          :loading="isLoading"
          :currency="lot.currency"
          :save-results="saveResultFullVolume" />
      <winner-selection-divisible-volume-dialog
          v-if="hasOrder && isDivisibleVolume"
          :result="result"
          :winner-orders="winnerOrders"
          :has-result="hasResult"
          :is-fix-premium="isFixPremium"
          :loading="isLoading"
          :save-results="saveResultDivisibleVolume" />
    </v-card-actions>
  </v-card>
</template>

<style scoped>
  .orders::v-deep td:nth-child(3) {
   word-break: break-all;
   max-width: 450px;
  }
  .orders::v-deep td:nth-child(1) {
    min-width: 200px;
  }
  .premiums::v-deep td:nth-child(1) {
    word-break: break-all;
    max-width: 550px;
  }
  .premiums::v-deep td:nth-child(2) {
    min-width: 150px;
  }
</style>

<script>
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import WinnerSelectionFullVolumeDialog from '@/components/reused/orders/WinnerSelectionFullVolumeDialog.vue'
  import WinnerSelectionDivisibleVolumeDialog from '@/components/reused/orders/WinnerSelectionDivisibleVolumeDialog.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import {
    TRADE_RESULT_TYPE_FULL_VOLUME_ENUM,
    TRADE_RESULT_TYPE_DIVISIBLE_VOLUME_ENUM
  } from '@/constants/enums'
  import { LOT_STATUSES, LOT_TABS, TRADE_RESULT_TYPES, PREMIUM_TYPES, LOT_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { LOT_MANAGEMENT, LOT_WINNERS_MANAGEMENT } from '@/constants/store.js'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import TradeResultFullVolume from '@/components/reused/orders/TradeResultFullVolume.vue'
  import TradeResultDivisibleVolume from '@/components/reused/orders/TradeResultDivisibleVolume.vue'

  export default {
    components: {
      GeneralError,
      WinnerSelectionFullVolumeDialog,
      WinnerSelectionDivisibleVolumeDialog,
      ValidatableEnumCombobox,
      TradeResultFullVolume,
      TradeResultDivisibleVolume
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      lotId: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        TRADE_RESULT_TYPES,
        isLoading: false,
        success: false,
        itemsPerPage: 5,
        nestedItemsPerPage: 5,
        result: {
          orders: [],
          tradeResultType: null
        },
        confirmDialog: {
          show: false,
          text: null,
          callback: null,
          warning: true
        }
      }
    },
    computed: {
      ...mapState(LOT_WINNERS_MANAGEMENT.NAMESPACE, {
        winnersInfo: state => (state[LOT_WINNERS_MANAGEMENT.STATE.WINNER_SELECTION_LIST] || {})
      }),
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        lot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT] || {})
      }),
      isDivisibleVolume () {
        return this.lot.lotType === LOT_TYPES.REQUEST_OFFERS_DIVISIBLE_VOLUME
      },
      tradeResultTypesEnum () {
        if (this.isAdmin) {
          return this.isDivisibleVolume ? TRADE_RESULT_TYPE_DIVISIBLE_VOLUME_ENUM : TRADE_RESULT_TYPE_FULL_VOLUME_ENUM
        } else {
          return {
            [TRADE_RESULT_TYPES.WINNER_SELECTED]: TRADE_RESULT_TYPE_FULL_VOLUME_ENUM[TRADE_RESULT_TYPES.WINNER_SELECTED],
            [TRADE_RESULT_TYPES.ONE_PARTICIPANT_AGREED]: TRADE_RESULT_TYPE_FULL_VOLUME_ENUM[TRADE_RESULT_TYPES.ONE_PARTICIPANT_AGREED]
          }
        }
      },
      headers1 () {
        let headers = [
          { text: this.$t('№ Сессии'), sortable: false, value: 'sessionNumber' },
          { text: this.$t('Организация'), sortable: false, value: 'organizationName' },
          { text: this.$t('Пользователь'), sortable: false, value: 'userName' },
          { text: this.$t('Время выставления'), sortable: false, value: 'orderDate' },
          { text: this.$t('Победитель'), sortable: false, value: 'isWinner' },
          { text: '', value: 'data-table-expand' }
        ]
        if (!this.isAdmin) {
          headers = headers.filter(h => h.value !== 'userName')
        }
        return headers
      },
      headers2 () {
        let headers = [
          { text: this.$t('Предмет договора'), sortable: false, value: 'positionDescription' },
          { text: this.$t(this.isFixPremium ? 'Цена 1 (одной) углеродной единицы (с учетом НДС)' : 'Значение дифференциала'), sortable: false, value: 'premium' },
          { text: this.$t('Общая стоимость торговой процедуры, {0}', [this.lot.currency]), sortable: false, value: 'orderCost' }
        ]
        if (!this.isFixPremium) {
          headers = headers.filter(h => h.value !== 'orderCost')
        }
        return headers
      },
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      orders () {
        const orders = this.winnersInfo.orders || []
        // меняем только копию из стора
        return orders.map(o => ({ ...o }))
      },
      winnerOrder () {
        return this.result.orders.length
          ? this.result.orders[0] || {}
          : {}
      },
      winnerOrders () {
        return this.result.orders.filter(o => o.isWinner)
      },
      hasResult () {
        const result = this.winnersInfo.tradeResultType
        return !!result && result !== TRADE_RESULT_TYPES.UNSET
      },
      hasOrder () {
        return this.orders.length > 0
      },
      canEditResults () {
        return this.isAdmin ||
          this.isLotOwner(this.lot)
      },
      canEdit () {
        return this.hasOrder &&
          (this.isAdmin ||
            (this.isLotOwner(this.lot) && !this.hasResult)) &&
          (this.lot.lotStatus === LOT_STATUSES.REGISTERED || this.lot.lotStatus === LOT_STATUSES.FINISHED)
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lotId) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_WINNERS_MANAGEMENT.NAMESPACE, {
        getWinnerSelectionListAction: LOT_WINNERS_MANAGEMENT.ACTIONS.GET_WINNER_SELECTION_LIST,
        selectWinnersAction: LOT_WINNERS_MANAGEMENT.ACTIONS.SELECT_WINNERS,
        updateWinnersAction: LOT_WINNERS_MANAGEMENT.ACTIONS.UPDATE_WINNERS,
        clearWinnerSelectionListAction: LOT_WINNERS_MANAGEMENT.ACTIONS.CLEAR_WINNER_SELECTION_LIST
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearWinnerSelectionListAction()
          ])
      },
      initialize: async function () {
        try {
          this.isLoading = true

          const model = {
            lotId: this.lotId,
            lotType: this.lotType
          }
          await this.getLotInfoAction(model)
          await this.getWinnerSelectionListAction(model)

          this.result.orders = this.orders
          this.result.tradeResultType = this.winnersInfo.tradeResultType || TRADE_RESULT_TYPES.UNSET
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      saveResultFullVolume: async function () {
        const winnerOrder = this.result.orders.find(w => w.isWinner)

        const model = {
          lotId: this.lotId,
          lotType: this.lotType,
          result: {
            orderId: winnerOrder?.orderId,
            tradeResultType: this.result.tradeResultType
          }
        }
        this.success = false
        try {
          this.isLoading = true

          if (!this.hasResult) {
            await this.selectWinnersAction(model)
          } else {
            await this.updateWinnersAction(model)
          }

          this.isLoading = false
          this.success = true
          await this.getWinnerSelectionListAction({
            lotId: this.lotId,
            lotType: this.lotType
          })
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      saveResultDivisibleVolume: async function () {
        const model = {
          lotId: this.lotId,
          lotType: this.lot.lotType,
          winnerType: this.lot.winnerType,
          result: {
            orders: this.winnerOrders.map(wo => {
              var o = { orderId: wo.orderId }
              if (this.lot.lotType === LOT_TYPES.REQUEST_OFFERS_DIVISIBLE_VOLUME) {
                o.reassignedSatisfiedVolume = wo.reassignedSatisfiedVolume
              }
              return o
            }),
            tradeResultType: this.result.tradeResultType
          }
        }
        this.success = false
        try {
          this.isLoading = true
          if (!this.hasResult) {
            await this.selectWinnersAction(model)
          } else {
            await this.updateWinnersAction(model)
          }
          this.isLoading = false
          this.success = true
          await this.getWinnerSelectionListAction({
            lotId: this.lotId,
            lotType: this.lotType,
            winnerType: this.lot.winnerType
          })
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      goToLot: function () {
        this.goToRoute(`${this.urlRootPath}/lot/${this.lotType}/${this.lotId}/${LOT_TABS.TRADE_RESULTS}`)
      }
    }
  }
</script>
